import {
  Button,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { StepTracker } from '@/shared/ui';
import { FORM_STEPS, FORM_STEP_ARRAY } from '../CreatePersonaForm/CreatePersonaForm.schema';
import { CreatePersonaFormProvider } from '../CreatePersonaForm/context/CreatePersonaFormProvider';
import { useCreatePersonaForm } from '../CreatePersonaForm/hooks/useCreatePersonaForm';
import { PersonaInformationStepV2, SpaceVisibilityStepV2 } from '../CreatePersonaForm/stepsV2';
import { RequiredFieldsLegendV2 } from '../PersonaFormSectionsV2';
import type { CreatePersonaSheetProps } from './CreatePersonaSheet.types';

/**
 * The two-step create-audience wizard, on a right-side sheet. Mount it only
 * while it should be open — the form seeds once, so unmounting is what resets it.
 *
 * Validation and submission live in `useCreatePersonaForm`, not here.
 */
export function CreatePersonaSheet({
  config,
  createPersona,
  onClose,
  onSuccess,
  onViewPersona,
}: CreatePersonaSheetProps) {
  const { t } = useTranslation('audiences');

  const form = useCreatePersonaForm({
    config,
    createPersona,
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onViewPersona,
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <Sheet open onOpenChange={(next) => !next && !isSubmitting && onClose?.()}>
      {/* The panel carries no padding of its own: `StepTracker` draws a full-bleed band with
          its own rule under it, which an inset would break into a floating line. */}
      <SheetContent className="flex w-[640px] flex-col gap-0 rounded-l-lg p-0 sm:max-w-none">
        <SheetHeader className="px-xl py-lg">
          <SheetTitle>{t('audiences.createPersonaSheet.createAudience')}</SheetTitle>
        </SheetHeader>

        <StepTracker
          activeStep={form.activeStepIndex}
          steps={FORM_STEP_ARRAY.map((step) => step.label)}
        />

        <CreatePersonaFormProvider form={form}>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-xl py-lg">
            {form.activeStepId === FORM_STEPS.personaInformation.id && <PersonaInformationStepV2 />}
            {form.activeStepId === FORM_STEPS.spaceVisibility.id && <SpaceVisibilityStepV2 />}
          </div>
        </CreatePersonaFormProvider>

        <SheetFooter className="flex-row items-center justify-between gap-sm px-xl py-lg">
          <RequiredFieldsLegendV2 />
          <div className="flex items-center gap-xs">
            <Button variant="outline" size="lg" disabled={!form.canGoBack} onClick={form.goBack}>
              {t('audiences.createPersonaSheet.back')}
            </Button>
            <Button
              size="lg"
              disabled={!form.canContinue || isSubmitting}
              onClick={() => void form.goNext()}
            >
              {form.isLastStep
                ? t('audiences.createPersonaSheet.createAudience')
                : t('audiences.createPersonaSheet.continue')}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

CreatePersonaSheet.displayName = 'CreatePersonaSheet';
